import { useMemo } from 'react';
import type { Metro } from '../../types/metro';
import { ratioColor } from '../../lib/map-utils';

/**
 * Per-occupational-group horizontal-bar comparison for the Compare
 * page. Renders one card per group present in the union of the
 * selected metros, each card containing N rows (one per selected
 * metro). Bar fill width = `min(ratio / 2.0, 1.0) * 100%` so the
 * 0-2.0 ratio range maps cleanly onto the track; ratios above 2.0
 * clamp visually and carry a "↑" annotation on the value label so
 * the overflow is honest. Tooltip on the bar surfaces the raw
 * unclamped ratio.
 *
 * Color choice: the same `ratioColor()` gradient used by Map bubbles
 * + Rankings severity pills (DEC-056 binary semantic, gradient
 * 0.0 rose -> 1.0 gold -> 1.8 green). Reusing the helper means
 * users reading three pages get one consistent color signal for
 * shortage vs surplus.
 *
 * Card chrome matches the GapCoverageCard "primary card" pattern
 * (PR #58): bg-white + shadow-card + rounded-lg + border-border +
 * p-4. NOT the NationalReferenceStat compact banner chrome.
 *
 * Conditional render: only when >= 2 metros are selected (1 metro =
 * nothing to compare). Empty-state hint card surfaces when fewer.
 *
 * Missing data: if a metro doesn't carry a row for a given group
 * (group exists in others but not this one), the row renders an
 * em-dash instead of a bar.
 */

interface CompareBarChartProps {
  readonly selectedMetros: ReadonlyArray<Metro | null>;
}

interface GroupRow {
  readonly metro: Metro;
  readonly ratio: number | null;
}

interface GroupCard {
  readonly groupName: string;
  readonly rows: ReadonlyArray<GroupRow>;
}

/**
 * Shorten a metro name for the row label column while preserving the
 * state abbreviation.
 *
 * 2026-07-02 (V2 feedback): reviewers wanted the state abbreviation
 * kept ("Baltimore, MD" not just "Baltimore") so cross-metro cards
 * disambiguate at a glance -- there are several "Springfields" and
 * "Columbias" in the 55-metro list. Strategy:
 *
 *   "Houston-The Woodlands-Sugar Land, TX"   -> "Houston, TX"
 *   "Baltimore-Columbia-Towson, MD"          -> "Baltimore, MD"
 *   "Bakersfield, CA"                        -> "Bakersfield, CA"
 *
 * We slice the primary city off the first `-` (falling back to the
 * whole city part when there's no hyphen), then re-attach the
 * trailing 2-letter state abbreviation found after the last comma.
 * If we can't find a 2-letter code we return the original string
 * unchanged rather than fabricate one.
 */
function shortenMetroName(name: string): string {
  const stateMatch = name.match(/,\s*([A-Z]{2}(?:-[A-Z]{2})*)\s*$/);
  const state = stateMatch?.[1];
  const cityPart = name.replace(/,\s*[A-Z]{2}(?:-[A-Z]{2})*\s*$/, '');
  const firstCity = cityPart.split('-')[0]?.trim() ?? cityPart;
  if (!state) return firstCity || name;
  return `${firstCity}, ${state}`;
}

/**
 * Ratio TEXT labels use a neutral foreground color (`text-text-2`),
 * not the `ratioColor()` gradient. The gold band of that gradient
 * (#F9A21C) at the `text-xs` 12px size fails WCAG AA contrast on a
 * white card background (2.06:1 vs the required 4.5:1), and the
 * 0.85-1.15 "near parity" zone -- exactly the most-interesting
 * numbers -- all land in that gold band.
 *
 * Precedent: `RatioChart.tsx:128` paints its ratio label `text-text-2`
 * and reserves `ratioColor()` for the decorative bar fill. The bar
 * here carries the same color signal, and the text value is always
 * legible alongside it, so no critical information is lost.
 */

export function CompareBarChart({ selectedMetros }: CompareBarChartProps) {
  const presentMetros = useMemo(
    () => selectedMetros.filter((m): m is Metro => m !== null),
    [selectedMetros],
  );

  const cards = useMemo<ReadonlyArray<GroupCard>>(() => {
    if (presentMetros.length < 2) return [];
    // Union of group names across the selected metros. Set keeps
    // ordering arrival; we sort alphabetically below for stable
    // vertical ordering as user changes metro picks.
    const names = new Set<string>();
    for (const m of presentMetros) {
      const groups = m.occupationGroups;
      if (!groups) continue;
      for (const g of groups) names.add(g.name);
    }
    const sorted = [...names].sort((a, b) => a.localeCompare(b));
    return sorted.map((groupName) => ({
      groupName,
      rows: presentMetros.map((m) => {
        const hit = (m.occupationGroups ?? []).find((g) => g.name === groupName);
        return { metro: m, ratio: hit ? hit.ratio : null };
      }),
    }));
  }, [presentMetros]);

  // v1.5 C3: footer missing-data note renders only when >=1 row has
  // null ratio (so it doesn't fire spuriously on complete comparisons).
  const hasMissingData = useMemo(
    () => cards.some((card) => card.rows.some((row) => row.ratio === null)),
    [cards],
  );

  if (presentMetros.length < 2) {
    return (
      <section
        aria-labelledby="compare-bar-chart-empty"
        className="rounded-lg border border-border bg-white p-6 text-sm text-text-3 shadow-card"
      >
        <h2
          id="compare-bar-chart-empty"
          className="mb-1 text-base font-semibold text-navy"
        >
          Per-group comparison
        </h2>
        <p>Select at least two metros above to see a per-occupational-group breakdown.</p>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="compare-bar-chart-heading"
      className="rounded-lg border border-border bg-white p-4 shadow-card"
    >
      <header className="mb-3 px-2">
        <h2
          id="compare-bar-chart-heading"
          className="text-base font-semibold text-navy"
        >
          Credentials-to-jobs ratio by occupational group
        </h2>
        {/* 2026-07-02 (V2 feedback): "clamp visually with ↑" reads as
            code-jargon. Rewrote as "are indicated with ↑" so the
            copy tracks the affordance without the jargon. */}
        <p className="mt-1 text-xs text-text-4">
          Bar length scales 0&ndash;2.0; the dashed line marks 1.0 parity.
          Bar color follows the same continuous shortage&ndash;surplus
          gradient used on the Map and Rankings. Values above 2.0 are
          indicated with &uarr;.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {cards.map((card) => (
          <article
            key={card.groupName}
            aria-label={`Comparison for ${card.groupName}`}
            className="rounded-md border border-border bg-white p-3"
          >
            <h3 className="mb-2 text-sm font-semibold text-navy">
              {card.groupName}
            </h3>
            <ul className="flex flex-col gap-1.5">
              {card.rows.map((row) => (
                <li
                  key={row.metro.id}
                  className="grid grid-cols-[6rem_minmax(0,1fr)_3.5rem] items-center gap-2"
                >
                  <span
                    className="truncate text-xs font-medium text-text-2"
                    title={row.metro.name}
                  >
                    {shortenMetroName(row.metro.name)}
                  </span>
                  {row.ratio === null ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="relative h-2.5 w-full rounded-full bg-ice"
                      />
                      <span className="text-right text-xs tabular-nums text-text-4">
                        &mdash;
                      </span>
                    </>
                  ) : (
                    <>
                      <BarTrack ratio={row.ratio} metroName={row.metro.name} groupName={card.groupName} />
                      <span
                        className="text-right text-xs font-semibold tabular-nums text-text-2"
                      >
                        {row.ratio.toFixed(2)}
                        {row.ratio > 2.0 && (
                          <span aria-label="value above chart scale"> &uarr;</span>
                        )}
                      </span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>

      {/* v1.5 C3: figure note explaining em-dash cells; gated on
          hasMissingData so it never fires on complete comparisons. */}
      {hasMissingData && (
        <p
          role="note"
          className="mt-3 px-2 text-xs italic leading-[1.5] text-text-4"
        >
          {/* 2026-07-02 (V2 feedback): "metro area" -> "metro areas"
              since the comparison always involves 2-3 metros. */}
          Missing data indicate that no springboard occupations were
          identified in this occupational group in the selected metro
          areas.
        </p>
      )}
    </section>
  );
}

interface BarTrackProps {
  readonly ratio: number;
  readonly metroName: string;
  readonly groupName: string;
}

/**
 * Single horizontal bar within a CompareBarChart row. Encapsulates
 * the width math, color, parity tick, and a11y labelling so the
 * parent grid stays readable.
 */
function BarTrack({ ratio, metroName, groupName }: BarTrackProps) {
  const widthPct = Math.min(ratio / 2.0, 1.0) * 100;
  const ariaLabel = `${groupName} in ${metroName}: ratio ${ratio.toFixed(2)}`;
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      title={`Ratio ${ratio.toFixed(2)}`}
      className="relative h-2.5 w-full overflow-hidden rounded-full bg-ice"
    >
      <div
        aria-hidden="true"
        className="h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none"
        style={{ width: `${widthPct}%`, backgroundColor: ratioColor(ratio) }}
      />
      {/* Parity tick at ratio 1.0 (= 50% of the 0-2.0 track). Drawn
          on top of the fill so it's always visible even when the bar
          extends past it. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-0 h-full w-px bg-navy/40"
        style={{ left: '50%' }}
      />
    </div>
  );
}

export default CompareBarChart;
